import { AutoColumn } from 'components/Column'
import { useState } from 'react'
import styled from 'styled-components/macro'

import { ButtonLight, ButtonPrimary } from '../components/Button'
import { ExternalLink, TYPE } from '../theme'
import { isEvmAddress, UZH_WALLET_URL } from './provider'

const Field = styled.div`
  display: flex;
  gap: 8px;
`

const AddressInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.bg3};
  background: ${({ theme }) => theme.bg1};
  color: ${({ theme }) => theme.text1};
  font-family: monospace;
  font-size: 14px;
`

const PasteButton = styled(ButtonLight)`
  width: auto;
  padding: 0 16px;
`

const Note = styled(TYPE.small)`
  color: ${({ theme }) => theme.red1};
  min-height: 1em;
`

export default function UzhWalletConnect({
  onConnectViaWallet,
  onUseAddress,
}: {
  onConnectViaWallet: () => void
  onUseAddress: (address: string) => void
}) {
  const [value, setValue] = useState('')
  const [note, setNote] = useState('')
  const trimmed = value.trim()
  const valid = isEvmAddress(trimmed)

  const paste = async () => {
    try {
      const match = /0x[0-9a-fA-F]{40}/.exec((await navigator.clipboard.readText()) || '')
      if (match) {
        setValue(match[0])
        setNote('')
      } else setNote('The clipboard holds no address.')
    } catch {
      setNote('Paste is blocked here: long-press the field and paste.')
    }
  }

  return (
    <AutoColumn gap="12px">
      <TYPE.mediumHeader>Use the UZH Wallet</TYPE.mediumHeader>
      <TYPE.body fontSize={14}>
        The UZH Wallet shares your address with this page; each transaction then opens in the wallet for you to confirm.
      </TYPE.body>
      <ButtonPrimary onClick={onConnectViaWallet}>Connect with the UZH Wallet</ButtonPrimary>
      <TYPE.body fontSize={14}>Or paste your address:</TYPE.body>
      <Field>
        <AddressInput
          value={value}
          placeholder="0x…"
          autoComplete="off"
          spellCheck={false}
          aria-label="Your address"
          onChange={(e) => {
            setValue(e.target.value)
            setNote(
              e.target.value.trim() && !isEvmAddress(e.target.value.trim())
                ? 'An address is 0x followed by 40 characters.'
                : ''
            )
          }}
        />
        <PasteButton onClick={paste}>Paste</PasteButton>
      </Field>
      <Note>{note}</Note>
      <ButtonLight disabled={!valid} onClick={() => valid && onUseAddress(trimmed)}>
        Use this address
      </ButtonLight>
      <ExternalLink href={UZH_WALLET_URL} style={{ textAlign: 'center', fontSize: 14 }}>
        Open the UZH Wallet to copy it
      </ExternalLink>
    </AutoColumn>
  )
}
